import { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { getURLParam, getPageFromPath, getDefaultSubtab, buildCreativesURL, parsePathParts } from "../utils/navigationUtils";

/**
 * Simplified URL state management hook
 * Single source of truth for URL parsing and navigation
 * Eliminates circular dependencies between other hooks
 */
export const useURLState = () => {
  const history = useHistory();
  const location = useLocation();

  // Parse current URL state once - supports both RESTful and legacy patterns
  const urlState = useMemo(() => {
    // Check if this is a RESTful URL pattern
    const pathParts = parsePathParts(location.pathname);
    const isRESTfulPath = pathParts.length >= 3 &&
                          pathParts[0] === 'creatives' &&
                          ['display', 'video'].includes(pathParts[1]) &&
                          ['units', 'groups'].includes(pathParts[2]);

    if (isRESTfulPath) {
      // Parse RESTful URL: /creatives/display/units or /creatives/video/groups
      const mediaType = pathParts[1]; // 'display' or 'video'
      const entityType = pathParts[2]; // 'units' or 'groups'

      const tab = mediaType === 'video' ? 'videoAds' : 'displayAds';
      const subtab = entityType === 'groups' ?
        (mediaType === 'video' ? 'adVideoGroups' : 'adGroups') :
        (mediaType === 'video' ? 'videos' : 'displayAdsUnits');

      const pageNumber = parseInt(getURLParam(location.search, 'page', '1'), 10);
      const returnUrl = getURLParam(location.search, 'returnUrl');

      return { tab, subtab, pageNumber, returnUrl };
    } else {
      // Legacy URL parsing with query parameters
      const tab = getURLParam(location.search, 'tab', 'displayAds');
      const subtab = getURLParam(location.search, 'subtab', getDefaultSubtab(tab));
      const pageNumber = getPageFromPath(location.pathname);
      const returnUrl = getURLParam(location.search, 'returnUrl');

      return { tab, subtab, pageNumber, returnUrl };
    }
  }, [location]);

  // Simple navigation function - single API for all navigation needs
  const updateURL = (newState, options = {}) => {
    const { type = 'replace' } = options;
    const {
      tab = urlState.tab,
      subtab = urlState.subtab,
      page = urlState.pageNumber
    } = newState;

    const newURL = buildCreativesURL(tab, subtab, page);
    history[type](newURL);
  };

  return {
    urlState,
    updateURL,
    // Direct access to router for advanced cases
    history,
    location
  };
};

export default useURLState;